import React from "react";
import LayoutHR from "layouts/layout-hr";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import CreateSchedulePatternPage from "./components/create-schedule-pattern-page";
import PrintJson from "utils/printJson";
import { ShiftsProvider } from "../master/context/ShiftsContext";
import { useSchedulePattern } from "./context/SchedulePatternContext";

const SchedulePatternsPage = () => {
  const { schedulePatterns, refetch, PaginationComponent } =
    useSchedulePattern();

  const { handleError, showCanvasHandler, handleSuccessToast } =
    useCanvasHandler();

  const handleCreate = (e) => {
    e.preventDefault();
    showCanvasHandler(
      "Create Schedule Pattern",
      <ShiftsProvider>
        <CreateSchedulePatternPage
          onSuccess={() => {
            handleSuccessToast(true, "Schedule pattern created successfully");
            refetch();
          }}
          onError={handleError}
        />
      </ShiftsProvider>,
    );
  };

  const handleUpdate = (e, data) => {
    e.preventDefault();
    showCanvasHandler(
      "Update Schedule Pattern",
      <ShiftsProvider>
        <CreateSchedulePatternPage
          initialData={data}
          onSuccess={() => {
            handleSuccessToast(true, "Schedule pattern updated successfully");
            refetch();
          }}
          onError={handleError}
        />
      </ShiftsProvider>,
    );
  };

  const handleDelete = async (e, id) => {
    e.preventDefault();
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}v1/ahr/schedule-patterns/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to delete schedule pattern");
        }

        handleSuccessToast(true, "Schedule pattern deleted successfully");
        refetch();
      } catch (error) {
        handleError(error);
      }
    }
  };

  if (schedulePatterns.loading) {
    return (
      <LayoutHR>
        <div
          className="container-xl d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </LayoutHR>
    );
  }

  if (schedulePatterns.error) {
    return (
      <LayoutHR>
        <div className="container-xl">
          <div className="alert alert-danger">
            Error loading schedule patterns data: {schedulePatterns.error}
          </div>
        </div>
      </LayoutHR>
    );
  }

  return (
    <LayoutHR>
      <div className="container-xl pt-3">
        {/* Table */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2>Schedule Patterns Management</h2>
              <button className="btn btn-primary" onClick={handleCreate}>
                Create Schedule Pattern
              </button>
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Total Days</th>
                    <th>Shift Details</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedulePatterns.data.length > 0 ? (
                    schedulePatterns.data.map((pattern, index) => (
                      <tr key={pattern.id}>
                        <td>
                          <strong>{pattern.name}</strong>
                        </td>
                        <td>{pattern.description || "-"}</td>
                        <td>
                          <span className="badge text-white bg-info">
                            {pattern.total_days} days
                          </span>
                        </td>
                        <td>
                          <div className="shift-details">
                            {pattern.details && pattern.details.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {pattern.details
                                  .sort((a, b) => a.day_order - b.day_order)
                                  .map((detail, idx) => (
                                    <small
                                      key={idx}
                                      className="badge bg-light text-dark border"
                                    >
                                      Day {detail.day_order}:{" "}
                                      {detail.shift?.name || "Unknown"}
                                    </small>
                                  ))}
                              </div>
                            ) : (
                              <span className="text-muted">
                                No shifts configured
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={(e) => handleUpdate(e, pattern)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={(e) => handleDelete(e, pattern.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No schedule patterns found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <PaginationComponent />
          </div>
        </div>
      </div>
      <PrintJson data={schedulePatterns.data} title="Schedule Patterns Data" />
    </LayoutHR>
  );
};

export default SchedulePatternsPage;
